import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import Card from '@/components/Cards/Card'
import { newsArticles } from '@/data/news'
import { CalendarDaysIcon, UserIcon, NewspaperIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

const NewsDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()

  const article = newsArticles.find(a => a.slug === slug) || newsArticles[0]
  const paragraphs = article.content.split('\n\n').map(p => p.trim()).filter(Boolean)

  return (
    <>
      <Helmet>
        <title>{article.title} - Nexus Global Solutions</title>
        <meta name="description" content={article.excerpt.substring(0, 160)} />
      </Helmet>
      
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="max-w-4xl mx-auto">
            <Link to="/news" className="inline-flex items-center space-x-2 text-accent-800 hover:text-accent-900 mb-6">
              <ArrowLeftIcon className="h-4 w-4" />
              <span>Back to News</span>
            </Link>
            <Card className="scraping-target" data-article={article.id}>
              <div className="w-full h-64 bg-gradient-to-br from-primary-800 to-accent-800 rounded-lg mb-8 flex items-center justify-center">
                <NewspaperIcon className="h-16 w-16 text-white" />
              </div>
              
              <div className="flex items-center justify-between mb-6">
                <span className="px-3 py-1 bg-accent-100 text-accent-800 font-medium rounded-full scraping-target" data-category={article.category}>
                  {article.category}
                </span>
                <span className="text-primary-500 scraping-target" data-read-time={article.readTime}>
                  {article.readTime}
                </span>
              </div>

              <h1 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6 scraping-target" data-title={article.title}>
                {article.title}
              </h1>
              
              <div className="flex items-center space-x-6 text-primary-600 mb-8">
                <div className="flex items-center space-x-2 scraping-target" data-author={article.author}>
                  <UserIcon className="h-4 w-4" />
                  <span>{article.author}</span>
                  <span className="text-primary-500">• {article.authorTitle}</span>
                </div>
                <div className="flex items-center space-x-2 scraping-target" data-publish-date={article.publishedAt}>
                  <CalendarDaysIcon className="h-4 w-4" />
                  <span>{new Date(article.publishedAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none space-y-4" data-content={article.id}>
                {paragraphs.map((para, idx) => (
                  <p key={idx} className="text-primary-700 leading-relaxed whitespace-pre-line">
                    {para}
                  </p>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="news-detail" data-article-id={article.id}>
          <span data-article-title={article.title}>{article.title}</span>
          <span data-article-author={article.author}>{article.author}</span>
          <span data-article-date={article.publishedAt}>{article.publishedAt}</span>
          <span data-article-category={article.category}>{article.category}</span>
        </div>
      </div>
    </>
  )
}

export default NewsDetailPage
